import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trash2 } from 'lucide-react';
import useProducts from '../../hooks/useProducts';

const SIZE_OPTIONS = ['0-3M', '3-6M', '6-12M', '1-2Y', '2-3Y', '3-4Y', '4-5Y', '5-6Y', '6-7Y'];

export default function ProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { getProduct, createProduct, updateProduct, deleteProducts } = useProducts();

  const [form, setForm] = useState({
    name: '', subtitle: '', category: 'Girls', sub: 'Girls', price: '', mrp: '',
    img: '', desc: '', tag: '', sizes: [], status: 'draft', stock: 0, trackInventory: false,
    emoji: '🌸', bg: 'linear-gradient(135deg,#f5ede4,#e8d8c8)', accent: '#c0735a', color: '', badge: '',
  });

  useEffect(() => {
    if (isEdit) {
      const product = getProduct(Number(id));
      if (product) {
        setForm({
          ...product,
          price: product.price.toString(),
          mrp: product.mrp.toString(),
          tag: product.tag || '',
        });
      }
    }
  }, [id, isEdit, getProduct]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleCategoryChange = (category) => {
    const emojiMap = { Girls: '🌸', Boys: '🌿', Baby: '👶' };
    updateField('category', category);
    updateField('sub', category);
    updateField('subtitle', category);
    updateField('badge', category);
    updateField('emoji', emojiMap[category] || '🎀');
  };

  const toggleSize = (size) => {
    setForm(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size) ? prev.sizes.filter(s => s !== size) : [...prev.sizes, size],
    }));
  };

  const handleSave = () => {
    if (!form.name || !form.price) { alert('Name and price are required'); return; }
    const data = {
      ...form,
      price: Number(form.price),
      mrp: Number(form.mrp) || Math.round(Number(form.price) * 1.25),
      tag: form.tag || null,
      color: form.color || form.category,
    };
    if (isEdit) { updateProduct(Number(id), data); }
    else { createProduct(data); }
    navigate('/admin');
  };

  const handleDelete = () => {
    if (window.confirm('Delete this product? This cannot be undone.')) {
      deleteProducts([Number(id)]);
      navigate('/admin');
    }
  };

  const discount = form.price && form.mrp ? Math.round((1 - Number(form.price) / Number(form.mrp)) * 100) : 0;

  const cardStyle = { background: '#fff', borderRadius: 14, border: '1px solid #ede3d5', padding: '22px', marginBottom: 18 };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 700, color: '#3d2b1f', marginBottom: 6 };
  const inputStyle = { width: '100%', padding: '10px 12px', border: '1.5px solid #ede3d5', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
        <button onClick={() => navigate('/admin')} className="admin-btn admin-btn-ghost"><ArrowLeft size={16} /></button>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, color: '#3d2b1f', fontStyle: 'italic' }}>
          {isEdit ? 'Edit Product' : 'Add Product'}
        </h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 18, alignItems: 'start' }}>
        <div>
          {/* Basic Info */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: '#3d2b1f', marginBottom: 16 }}>Basic Information</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Product Name</label>
              <input style={inputStyle} value={form.name} onChange={(e) => updateField('name', e.target.value)} placeholder="e.g. Cotton Frock (2-3 y)" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Description</label>
              <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={form.desc} onChange={(e) => updateField('desc', e.target.value)} placeholder="Product description..." />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Category</label>
                <select style={inputStyle} value={form.category} onChange={(e) => handleCategoryChange(e.target.value)}>
                  <option>Girls</option><option>Boys</option><option>Baby</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Subcategory</label>
                <input style={inputStyle} value={form.sub} onChange={(e) => updateField('sub', e.target.value)} placeholder="e.g. Dresses, Coords" />
              </div>
            </div>
          </div>

          {/* Media */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: '#3d2b1f', marginBottom: 16 }}>Media</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Image Path</label>
              <input style={inputStyle} value={form.img} onChange={(e) => updateField('img', e.target.value)} placeholder="/catalog/girls/17.jpg" />
            </div>
            {form.img && (
              <div style={{ width: 120, height: 120, borderRadius: 10, overflow: 'hidden', border: '1px solid #ede3d5' }}>
                <img src={form.img} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            )}
          </div>

          {/* Pricing */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: '#3d2b1f', marginBottom: 16 }}>Pricing</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Selling Price (₹)</label>
                <input style={inputStyle} type="number" value={form.price} onChange={(e) => updateField('price', e.target.value)} placeholder="650" />
              </div>
              <div>
                <label style={labelStyle}>MRP (₹)</label>
                <input style={inputStyle} type="number" value={form.mrp} onChange={(e) => updateField('mrp', e.target.value)} placeholder="813" />
              </div>
              <div>
                <label style={labelStyle}>Discount</label>
                <div style={{ ...inputStyle, background: '#faf6f0', display: 'flex', alignItems: 'center', color: discount > 0 ? '#4a7358' : '#7a5c4a', fontWeight: 700 }}>
                  {discount > 0 ? `${discount}% off` : '—'}
                </div>
              </div>
            </div>
          </div>

          {/* Inventory */}
          <div style={cardStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: '#3d2b1f', marginBottom: 16 }}>Inventory</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <input type="checkbox" checked={form.trackInventory} onChange={(e) => updateField('trackInventory', e.target.checked)} style={{ cursor: 'pointer' }} id="track-inv" />
              <label htmlFor="track-inv" style={{ fontSize: 13, fontWeight: 700, color: '#3d2b1f', cursor: 'pointer' }}>Track quantity</label>
            </div>
            {form.trackInventory && (
              <div>
                <label style={labelStyle}>Stock Quantity</label>
                <input style={{ ...inputStyle, width: 120 }} type="number" min="0" value={form.stock} onChange={(e) => updateField('stock', Number(e.target.value))} />
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>Available Sizes</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {SIZE_OPTIONS.map(s => (
                  <button key={s} type="button" onClick={() => toggleSize(s)}
                    style={{
                      padding: '6px 12px', borderRadius: 8, border: '1.5px solid', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                      borderColor: form.sizes.includes(s) ? '#c0735a' : '#ede3d5',
                      background: form.sizes.includes(s) ? '#f0d5cb' : '#fff',
                      color: form.sizes.includes(s) ? '#c0735a' : '#7a5c4a',
                    }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div>
          <div style={cardStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: '#3d2b1f', marginBottom: 16 }}>Status</h3>
            {['active', 'draft', 'archived'].map(s => (
              <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#3d2b1f', textTransform: 'capitalize' }}>
                <input type="radio" name="status" checked={form.status === s} onChange={() => updateField('status', s)} style={{ cursor: 'pointer' }} />
                {s}
              </label>
            ))}
          </div>

          <div style={cardStyle}>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: '#3d2b1f', marginBottom: 16 }}>Product Tag</h3>
            <select style={inputStyle} value={form.tag} onChange={(e) => updateField('tag', e.target.value)}>
              <option value="">None</option>
              <option value="Bestseller">Bestseller</option>
              <option value="New">New</option>
              <option value="Popular">Popular</option>
              <option value="Limited">Limited</option>
            </select>
          </div>

          {isEdit && (
            <div style={{ ...cardStyle, borderColor: '#ffcccc' }}>
              <button type="button" onClick={handleDelete}
                style={{ background: 'none', border: 'none', color: '#dc3545', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }}>
                <Trash2 size={14} /> Delete product
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ position: 'sticky', bottom: 0, background: '#fff', borderTop: '1px solid #ede3d5', padding: '14px 0', marginTop: 10, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button type="button" className="admin-btn admin-btn-ghost" onClick={() => navigate('/admin')}>Discard</button>
        <button type="button" className="admin-btn admin-btn-primary" onClick={handleSave}>
          {isEdit ? 'Save Changes' : 'Create Product'}
        </button>
      </div>
    </div>
  );
}
